// src/DashboardTables.js
import React, { useState, useEffect } from 'react';
import api, { API_BASE } from './apiClient';

import LowPriorityIcon from '@mui/icons-material/LowPriority';
import MediumPriorityIcon from '@mui/icons-material/PriorityHigh';
import HighPriorityIcon from '@mui/icons-material/Whatshot';
import SettingsIcon from '@mui/icons-material/Settings';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import LockIcon from '@mui/icons-material/Lock';

import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import TaskAssignment from './TaskAssignment';

/* ---------- helpers for sortable tables ---------- */

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

/* ---------- reusable constants ---------- */

const PRIORITIES = ['low', 'medium', 'high'];

// columns that user can toggle in home cards
const HOME_TABLE_AVAILABLE_COLUMNS = [
  { id: 'title', label: 'Title' },
  { id: 'priority', label: 'Priority' },
  { id: 'due_date', label: 'Due Date' },
  { id: 'status', label: 'Status' },
];

const formatDateDisplay = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(d);
};

/* ---------- Due-date health helper ---------- */

function getDueHealth(dueDate, status) {
  if (!dueDate) {
    return { rowBg: 'inherit', label: '', labelColor: 'text.secondary' };
  }
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) {
    return { rowBg: 'inherit', label: '', labelColor: 'text.secondary' };
  }

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfDue = new Date(
    due.getFullYear(),
    due.getMonth(),
    due.getDate()
  );

  const diffMs = startOfDue - startOfToday;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const plural = (n) => (n === 1 ? '' : 's');
  const normalizedStatus = String(status || '').toLowerCase();

  // For completed/closed tasks: show text but do not highlight
  if (normalizedStatus === 'completed' || normalizedStatus === 'closed') {
    let label = '';
    if (diffDays < 0) {
      label = `Was overdue by ${Math.abs(diffDays)} day${plural(
        Math.abs(diffDays)
      )}`;
    } else if (diffDays === 0) {
      label = 'Was due today';
    } else {
      label = `Was due in ${diffDays} day${plural(diffDays)}`;
    }
    return {
      rowBg: 'inherit',
      label,
      labelColor: 'text.secondary',
    };
  }

  // Open tasks - softer pastels
  if (diffDays < 0) {
    const d = Math.abs(diffDays);
    return {
      rowBg: 'rgba(248, 113, 113, 0.04)', // very soft red
      label: `Overdue by ${d} day${plural(d)}`,
      labelColor: 'error.main',
    };
  }
  if (diffDays === 0) {
    return {
      rowBg: 'rgba(251, 191, 36, 0.04)', // very soft amber
      label: 'Due today',
      labelColor: 'warning.main',
    };
  }
  return {
    rowBg: 'inherit',
    label: `Due in ${diffDays} day${plural(diffDays)}`,
    labelColor: 'text.secondary',
  };
}

/* ---------- User display helpers ---------- */

const getUploadsBase = () => {
  if (API_BASE && API_BASE.startsWith('http')) return API_BASE;
  if (api?.defaults?.baseURL && /^https?:/i.test(api.defaults.baseURL))
    return api.defaults.baseURL;
  return window.location.origin;
};

const buildUserDisplay = (user = {}) => {
  if (typeof user === 'string') {
    const parts = user.trim().split(/\s+/).filter(Boolean);
    const firstName = parts[0] || user || 'User';
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
    return buildUserDisplay({ first_name: firstName, last_name: lastName });
  }

  const firstName =
    user.first_name ||
    user.firstname ||
    user.firstName ||
    user.username ||
    user.email ||
    '';
  const lastName = user.last_name || user.lastname || user.lastName || '';
  const username = user.username || user.email || '';
  const fullName =
    `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim() ||
    username ||
    'User';

  const words = (firstName || '').trim().split(/\s+/).filter(Boolean);
  let initials = '';
  if (firstName && lastName) {
    initials = `${firstName[0]}${lastName[0]}`;
  } else if (words.length >= 2) {
    initials = `${words[0][0]}${words[1][0]}`;
  } else if (words.length === 1) {
    initials = words[0][0];
  } else if (username) {
    initials = username[0];
  }
  initials = initials.toUpperCase();

  const avatarSrc =
    user.avatar ||
    user.avatar_url ||
    user.profile_pic ||
    user.profilePic ||
    user.photo_url ||
    (user.photo ? `${getUploadsBase()}/uploads/${user.photo}` : null) ||
    null;

  return { firstName: firstName || username || 'User', lastName, fullName, initials, avatarSrc };
};

const avatarPalette = ['#00897B', '#2563EB', '#7C3AED', '#EA580C', '#0EA5E9', '#E11D48', '#0F766E'];
const pickAvatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};

const UserAvatar = ({ user, size = 28, showName = false }) => {
  const data = buildUserDisplay(user || {});
  const bg = pickAvatarColor(data.fullName || data.initials || 'u');
  return (
    <Tooltip title={data.fullName} placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: showName ? 0.75 : 0 }}>
        <Avatar
          src={data.avatarSrc || undefined}
          sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}
        >
          {!data.avatarSrc && data.initials}
        </Avatar>
        {showName && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {data.firstName}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const resolveUserById = (id, users = []) =>
  users.find((u) => Number(u.id) === Number(id));

/* ---------- Task tags helper ---------- */

function getTaskTags(task) {
  if (!task) return [];
  const raw = task.tags;

  if (Array.isArray(raw)) {
    return raw
      .map((t) => String(t).trim())
      .filter((t) => t.length > 0);
  }

  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }

  return [];
}

/* ---------- Task lock helper (pending approval / closed) ---------- */

function isTaskLocked(task) {
  if (!task) return false;
  const status = String(task.status || '').toLowerCase();
  const completionState = String(task.completion_state || '').toLowerCase();

  const isPendingApproval =
    status === 'completed' && completionState === 'pending';
  const isClosed = status === 'closed';

  return isPendingApproval || isClosed;
}

/* ---------- Status pill helper ---------- */

function StatusPill({ status }) {
  const key = (status || '').toLowerCase();
  const map = {
    'to-do': {
      bg: 'rgba(148, 163, 184, 0.14)',
      color: '#475569',
    },
    'in-progress': {
      bg: 'rgba(96, 165, 250, 0.18)',
      color: '#1d4ed8',
    },
    completed: {
      bg: 'rgba(34, 197, 94, 0.16)',
      color: '#15803d',
    },
    closed: {
      bg: 'rgba(148, 163, 184, 0.18)',
      color: '#4b5563',
    },
  };

  const cfg = map[key] || map['to-do'];
  const label = (status || '').replace(/_/g, ' ') || 'To-do';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.2,
        py: 0.3,
        borderRadius: 999,
        bgcolor: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'capitalize',
        gap: 0.6,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: cfg.color,
        }}
      />
      {label}
    </Box>
  );
}

// Status style helper for colourful table rows and chips
function getStatusStyle(status) {
  const key = (status || '').toLowerCase();
  const map = {
    'to-do': {
      label: 'To-Do',
      color: '#7C3AED',
      bg: 'rgba(124, 58, 237, 0.08)',
    },
    'in-progress': {
      label: 'In Progress',
      color: '#F59E0B',
      bg: 'rgba(245, 158, 11, 0.10)',
    },
    completed: {
      label: 'Completed',
      color: '#10B981',
      bg: 'rgba(16, 185, 129, 0.10)',
    },
    closed: {
      label: 'Closed',
      color: '#94A3B8',
      bg: 'rgba(148, 163, 184, 0.10)',
    },
  };
  return map[key] || map['to-do'];
}

/**
 * Generic task table used on Home and fallback personal view.
 * Card-style rows + status pill + soft colours.
 */
function TaskTable({
  title,
  tasks = [],
  onModify, // currently unused but kept for compatibility
  columns,
  availableColumns,
  onColumnsChange,
  userId,
  getUserName,
  assignableUsers,
  onRowClick, // optional
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('due_date');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);

  const DEFAULT_COLUMNS = ['title', 'priority', 'due_date'];
  const activeColumns =
    columns && columns.length ? columns : DEFAULT_COLUMNS;
  const allColumns = availableColumns || HOME_TABLE_AVAILABLE_COLUMNS;

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedTasks = stableSort(tasks, getComparator(order, orderBy));

  const handleOpenColumnsMenu = (event) => {
    if (!onColumnsChange) return;
    setColumnsMenuAnchor(event.currentTarget);
  };

  const handleCloseColumnsMenu = () => {
    setColumnsMenuAnchor(null);
  };

  const toggleColumn = (colId) => {
    if (!onColumnsChange) return;
    if (colId === 'title') return; // Title is mandatory
    const current = columns && columns.length ? columns : DEFAULT_COLUMNS;
    const exists = current.includes(colId);
    let next;
    if (exists) {
      next = current.filter((c) => c !== colId);
    } else {
      next = [...current, colId];
    }
    onColumnsChange(next);
  };

  // eslint-disable-next-line no-unused-vars
  const formatDate = (val) => formatDateDisplay(val);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        ml: 0,
        mr: 0,
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {onColumnsChange && (
            <>
              <IconButton size="small" onClick={handleOpenColumnsMenu}>
                <SettingsIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={handleCloseColumnsMenu}
              >
                {allColumns.map((col) => (
                  <MenuItem
                    key={col.id}
                    onClick={() => toggleColumn(col.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={activeColumns.includes(col.id)}
                      disabled={col.id === 'title'}
                    />
                    <ListItemText primary={col.label} />
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {tasks.length} item(s)
        </Typography>
      </Box>

      {/* Card-style table */}
      <TableContainer
        sx={{
          width: '100%', // keep table stretched across available width
          backgroundColor: 'transparent',
          boxShadow: 'none',
          pb: 1,
          maxHeight: 'calc(100vh - 320px)',
          overflowY: 'auto',
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: '0 12px',
            tableLayout: 'fixed',
            width: '100%',
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  borderBottom: 'none',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: 'text.secondary',
                  fontWeight: 600,
                  paddingY: 0.5,
                },
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedTasks.length > 0 &&
                    selectedTasks.length < sortedTasks.length
                  }
                  checked={
                    sortedTasks.length > 0 &&
                    selectedTasks.length === sortedTasks.length
                  }
                  onChange={(e) =>
                    setSelectedTasks(
                      e.target.checked ? sortedTasks.map((t) => t.id) : []
                    )
                  }
                />
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'title' ? order : false}
                sx={{ width: '30%' }}
              >
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleRequestSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'status' ? order : false}
                sx={{ width: '10%' }}
              >
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'created_at' ? order : false}
                sx={{ width: '12%' }}
              >
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'target_date' ? order : false}
                sx={{ width: '12%' }}
              >
                <TableSortLabel
                  active={orderBy === 'target_date'}
                  direction={orderBy === 'target_date' ? order : 'asc'}
                  onClick={() => handleRequestSort('target_date')}
                >
                  Target Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'due_date' ? order : false}
                sx={{ width: '12%' }}
              >
                <TableSortLabel
                  active={orderBy === 'due_date'}
                  direction={orderBy === 'due_date' ? order : 'asc'}
                  onClick={() => handleRequestSort('due_date')}
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '16%' }}>Created By</TableCell>
              <TableCell sx={{ width: '12%' }}>Assigned To</TableCell>
              <TableCell sx={{ width: '12%' }}>Collaborators</TableCell>
              <TableCell
                sortDirection={orderBy === 'priority' ? order : false}
                sx={{ width: '12%' }}
              >
                <TableSortLabel
                  active={orderBy === 'priority'}
                  direction={orderBy === 'priority' ? order : 'asc'}
                  onClick={() => handleRequestSort('priority')}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell>Tags</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedTasks.map((task) => {
              const dueInfo = getDueHealth(task.due_date, task.status);
              const tagsArray = getTaskTags(task);

              return (
                <TableRow
                  key={task.id}
                  hover
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(task);
                    } else {
                      console.log('Task clicked:', task);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    bgcolor:
                      dueInfo.rowBg === 'inherit'
                        ? '#ffffff'
                        : dueInfo.rowBg,
                  boxShadow: '0 12px 28px rgba(15,23,42,0.05)',
                  borderRadius: 2,
                  '& td': {
                    borderBottom: 'none',
                    py: 1.4,
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                    '& td:first-of-type': {
                      borderTopLeftRadius: 16,
                      borderBottomLeftRadius: 16,
                    },
                    '& td:last-of-type': {
                      borderTopRightRadius: 16,
                      borderBottomRightRadius: 16,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedTasks.includes(task.id)}
                      onChange={(e) =>
                        setSelectedTasks((prev) =>
                          e.target.checked
                            ? [...prev, task.id]
                            : prev.filter((id) => id !== task.id)
                        )
                      }
                    />
                  </TableCell>

                  {/* Title */}
                  <TableCell sx={{ maxWidth: '30%' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.title}
                    </Typography>
                  </TableCell>

                  {/* Status pill */}
                  <TableCell sx={{ width: '10%' }}>
                    <StatusPill status={task.status} />
                  </TableCell>

                  {/* Created */}
                  <TableCell sx={{ width: '12%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserAvatar
                        user={
                          resolveUserById(task.user_id, assignableUsers) ||
                          (typeof getUserName === 'function'
                            ? { username: getUserName(task.user_id) }
                            : { username: `User ${task.user_id}` })
                        }
                        size={28}
                      />
                      <Typography variant="body2">
                        {(typeof getUserName === 'function'
                          ? getUserName(task.user_id)
                          : '') ||
                          (resolveUserById(task.user_id, assignableUsers)?.firstName ||
                            resolveUserById(task.user_id, assignableUsers)?.username ||
                            `User ${task.user_id}`)}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Target date */}
                  <TableCell sx={{ width: '12%' }}>{formatDateDisplay(task.target_date)}</TableCell>

                  {/* Due date + helper text */}
                  <TableCell sx={{ width: '12%' }}>
                    <Typography variant="body2">
                      {formatDateDisplay(task.due_date) || '—'}
                    </Typography>
                    {dueInfo.label && (
                      <Typography variant="caption" color={dueInfo.labelColor}>
                        {dueInfo.label}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Created by */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserAvatar
                        user={
                          resolveUserById(task.user_id, assignableUsers) ||
                          (typeof getUserName === 'function'
                            ? { username: getUserName(task.user_id) }
                            : { username: `User ${task.user_id}` })
                        }
                        size={28}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(typeof getUserName === 'function'
                          ? getUserName(task.user_id)
                          : '') ||
                          resolveUserById(task.user_id, assignableUsers)?.firstName ||
                          resolveUserById(task.user_id, assignableUsers)?.username ||
                          `User ${task.user_id}`}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Assigned to (inline dropdown) */}
                  <TableCell sx={{ width: '12%', maxWidth: '14%', pr: 1, minWidth: 120 }}>
                    <TaskAssignment
                      taskId={task.id}
                      assignedTo={task.assigned_to}
                      users={assignableUsers || []}
                      onAssign={() => {}}
                      currentUserId={userId}
                      compact
                    />
                  </TableCell>

                  {/* Collaborators */}
                  <TableCell sx={{ width: '12%', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {Array.isArray(task.collaborators) && task.collaborators.length > 0 ? (
                        task.collaborators.map((c) => (
                          <UserAvatar key={c.id || c.username} user={c} size={24} />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Priority with icon */}
                  <TableCell sx={{ width: '12%' }}>
                    {(() => {
                      const priorityConfig = {
                        low: {
                          icon: <LowPriorityIcon sx={{ color: '#4caf50' }} />,
                          label: 'Low',
                        },
                        medium: {
                          icon: (
                            <MediumPriorityIcon sx={{ color: '#ff9800' }} />
                          ),
                          label: 'Medium',
                        },
                        high: {
                          icon: <HighPriorityIcon sx={{ color: '#f44336' }} />,
                          label: 'High',
                        },
                      };
                      const config =
                        priorityConfig[task.priority || 'medium'];
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {config.icon}
                          <Typography variant="body2">{config.label}</Typography>
                        </Box>
                      );
                    })()}
                  </TableCell>

                  {/* Tags */}
                  <TableCell>
                    {tagsArray.length > 0 ? tagsArray.join(', ') : '—'}
                  </TableCell>
                </TableRow>
              );
            })}

            {sortedTasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No tasks in this project yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/* ---------- Simple list card ---------- */

function SimpleListCard({ title, items = [], getLabel }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {items.length} item(s)
        </Typography>
      </Box>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item.id} sx={{ px: 2, py: 0.5 }}>
            <ListItemText primary={getLabel(item)} />
          </ListItem>
        ))}
        {items.length === 0 && (
          <ListItem sx={{ px: 2, py: 1 }}>
            <ListItemText
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
              }}
              primary="Nothing to show yet."
            />
          </ListItem>
        )}
      </List>
    </Paper>
  );
}

/* ---------- Recurring tasks table ---------- */

function RecurringTasksTable({ rows, showProject, onAction }) {
  const statusColorMap = {
    active: 'success.main',
    paused: 'warning.main',
    stopped: 'text.secondary',
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Task / Template</TableCell>
            {showProject && <TableCell>Project</TableCell>}
            <TableCell>Rule</TableCell>
            <TableCell>Next run</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const status = (r.status || '').toLowerCase() || 'active';
            const color = statusColorMap[status] || 'text.secondary';
            const title =
              r.task_title ||
              r.template_title ||
              r.title ||
              `Rule #${r.id}`;
            const projectName =
              r.project_name ||
              r.project_title ||
              '';
            const ruleText =
              r.schedule_text ||
              r.rule_summary ||
              r.rule ||
              r.pattern ||
              '—';
            const nextRun =
              r.next_run_on ||
              r.next_run_date ||
              r.next_run_at;

            return (
              <TableRow key={r.id}>
                <TableCell>{title}</TableCell>
                {showProject && <TableCell>{projectName}</TableCell>}
                <TableCell>{ruleText}</TableCell>
                <TableCell>{formatDateDisplay(nextRun)}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color,
                      textTransform: 'capitalize',
                    }}
                  >
                    {status || 'active'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => onAction(r.id, 'skip')}
                      title="Skip next"
                    >
                      <SkipNextIcon fontSize="small" />
                    </IconButton>
                    {status !== 'paused' && status !== 'stopped' && (
                      <IconButton
                        size="small"
                        onClick={() => onAction(r.id, 'pause')}
                        title="Pause"
                      >
                        <PauseCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                    {status === 'paused' && (
                      <IconButton
                        size="small"
                        onClick={() => onAction(r.id, 'resume')}
                        title="Resume"
                      >
                        <PlayCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                    {status !== 'stopped' && (
                      <IconButton
                        size="small"
                        onClick={() => onAction(r.id, 'stop')}
                        title="Stop"
                      >
                        <StopCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={showProject ? 6 : 5} align="center">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No recurring rules defined yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ---------- Project tasks table (project view) ---------- */

function ProjectTasksTable({
  tasks = [],
  onRowClick,
  getUserName,
  userId,
  assignableUsers = [],
  setTasks,
  handleModify,
  taskStatusFilter,
  setTaskStatusFilter,
  taskPriorityFilter,
  setTaskPriorityFilter,
  taskAssigneeFilter,
  setTaskAssigneeFilter,
  taskTagFilter,
  setTaskTagFilter,
  taskDuePreset = 'all',
  setTaskDuePreset = () => {},
  taskTargetPreset = 'all',
  setTaskTargetPreset = () => {},
  taskCreatorFilter = 'all',
  setTaskCreatorFilter = () => {},
  taskDueCustomFrom = '',
  taskDueCustomTo = '',
  setTaskDueCustomFrom = () => {},
  setTaskDueCustomTo = () => {},
  taskTargetCustomFrom = '',
  taskTargetCustomTo = '',
  setTaskTargetCustomFrom = () => {},
  setTaskTargetCustomTo = () => {},
  taskQuickSearch = '',
  setTaskQuickSearch = () => {},
  availableTags = [],
  showArchived,
  onToggleArchived,
  onClearFilters,
  canApproveCompletions = false,
  onApproveCompletion,
  savedTaskViews = [],
  activeTaskViewId = null,
  savedViewLoading = false,
  onSelectTaskView = () => {},
  onSaveTaskView = () => {},
  onDeleteTaskView = () => {},
  columnScopeKey = 'global',
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('due_date'); // default sort
  const columnKey = `taskColumns:${columnScopeKey}`;
  const defaultColumns = {
    title: true,
    status: true,
    created: true,
    targetDate: true,
    dueDate: true,
    createdBy: true,
    assignedTo: true,
    collaborators: true,
    priority: true,
    tags: true,
    actions: true,
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // Use Dashboard's handleModify to update a single field
  const updateField = (taskId, field, value) => {
    if (typeof handleModify === 'function') {
      handleModify(taskId, field, value);
    } else if (setTasks) {
      // Fallback: local-only update if handleModify not passed
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, [field]: value } : t))
      );
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Load / persist column visibility
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(columnKey) || 'null');
      if (saved && typeof saved === 'object') {
        setVisibleColumns({ ...defaultColumns, ...saved });
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKey]);

  useEffect(() => {
    try {
      localStorage.setItem(columnKey, JSON.stringify(visibleColumns));
    } catch {
      /* ignore */
    }
  }, [columnKey, visibleColumns]);

  const showCol = (key) => {
    if (key === 'actions' && !canApproveCompletions) return false;
    return visibleColumns[key] !== false;
  };

  const visibleColCount =
    Object.entries(defaultColumns).reduce((acc, [k]) => {
      return acc + (showCol(k) ? 1 : 0);
    }, 0) || 1;

  const formatDateIso = (val) => (val ? String(val).slice(0, 10) : '');
  const sortedTasks = stableSort(tasks, getComparator(order, orderBy));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 1,
          columnGap: 1,
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Tasks
          </Typography>

          {typeof onClearFilters === 'function' && (
            <Button size="small" variant="text" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}

          {typeof onToggleArchived === 'function' && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!showArchived}
                  onChange={(e) => onToggleArchived(e.target.checked)}
                />
              }
              label="Show archived"
              sx={{ ml: 0 }}
            />
          )}
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'right' }}
        >
          {tasks.length} item(s)
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            gridColumn: '1 / span 2',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Saved view</InputLabel>
            <Select
              label="Saved view"
              value={activeTaskViewId || ''}
              onChange={(e) => onSelectTaskView(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {(savedTaskViews || []).map((view) => (
                <MenuItem key={view.id} value={view.id}>
                  {view.name}
                  {view.is_default ? ' (Default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            onClick={() => onSaveTaskView({ update: false })}
          >
            Save as new
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={!activeTaskViewId}
            onClick={() => onSaveTaskView({ update: true })}
          >
            Update
          </Button>
          <Button
            size="small"
            color="error"
            variant="text"
            disabled={!activeTaskViewId}
            onClick={onDeleteTaskView}
          >
            Delete
          </Button>
          {savedViewLoading && <CircularProgress size={16} />}

          <Tooltip title="Choose columns">
            <IconButton
              size="small"
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              sx={{ ml: 'auto' }}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={() => setColumnMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
          >
            {[
              { key: 'title', label: 'Title' },
              { key: 'status', label: 'Status' },
              { key: 'created', label: 'Created' },
              { key: 'targetDate', label: 'Target Date' },
              { key: 'dueDate', label: 'Due Date' },
              { key: 'createdBy', label: 'Created By' },
              { key: 'assignedTo', label: 'Assigned To' },
              { key: 'collaborators', label: 'Collaborators' },
              { key: 'priority', label: 'Priority' },
              { key: 'tags', label: 'Tags' },
              ...(canApproveCompletions ? [{ key: 'actions', label: 'Actions' }] : []),
            ].map((col) => (
              <MenuItem
                key={col.key}
                onClick={() =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col.key]: !prev[col.key],
                  }))
                }
              >
                <Checkbox size="small" checked={showCol(col.key)} />
                <ListItemText primary={col.label} />
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {setTaskStatusFilter && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              gridColumn: '1 / span 2',
            }}
          >
            <TextField
              size="small"
              placeholder="Search in tasks"
              value={taskQuickSearch}
              onChange={(e) => setTaskQuickSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="to-do">To-Do</MenuItem>
                <MenuItem value="in-progress">In-Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending-approval">
                  Completed (awaiting approval)
                </MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={taskPriorityFilter}
                onChange={(e) => setTaskPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Assignee</InputLabel>
              <Select
                label="Assignee"
                value={taskAssigneeFilter}
                onChange={(e) => setTaskAssigneeFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {assignableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Tag</InputLabel>
              <Select
                label="Tag"
                value={taskTagFilter}
                onChange={(e) => setTaskTagFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {(availableTags || []).map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Due Date</InputLabel>
              <Select
                label="Due Date"
                value={taskDuePreset}
                onChange={(e) => setTaskDuePreset(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="tomorrow">Tomorrow</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="next_week">Next Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="next_month">Next Month</MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>

            {taskDuePreset === 'custom' && (
              <>
                <TextField
                  type="date"
                  size="small"
                  label="Due from"
                  InputLabelProps={{ shrink: true }}
                  value={taskDueCustomFrom}
                  onChange={(e) => setTaskDueCustomFrom(e.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  label="Due to"
                  InputLabelProps={{ shrink: true }}
                  value={taskDueCustomTo}
                  onChange={(e) => setTaskDueCustomTo(e.target.value)}
                />
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Target Date</InputLabel>
              <Select
                label="Target Date"
                value={taskTargetPreset}
                onChange={(e) => setTaskTargetPreset(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="tomorrow">Tomorrow</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
                <MenuItem value="next_week">Next Week</MenuItem>
                <MenuItem value="this_month">This Month</MenuItem>
                <MenuItem value="next_month">Next Month</MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
              </Select>
            </FormControl>

            {taskTargetPreset === 'custom' && (
              <>
                <TextField
                  type="date"
                  size="small"
                  label="Target from"
                  InputLabelProps={{ shrink: true }}
                  value={taskTargetCustomFrom}
                  onChange={(e) => setTaskTargetCustomFrom(e.target.value)}
                />
                <TextField
                  type="date"
                  size="small"
                  label="Target to"
                  InputLabelProps={{ shrink: true }}
                  value={taskTargetCustomTo}
                  onChange={(e) => setTaskTargetCustomTo(e.target.value)}
                />
              </>
            )}

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Created By</InputLabel>
              <Select
                label="Created By"
                value={taskCreatorFilter}
                onChange={(e) => setTaskCreatorFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {assignableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username || u.email || `User ${u.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* filters rendered in header grid above */}

      <TableContainer
        sx={{
          width: '100%', // stretch table across the container in all views
          backgroundColor: 'transparent',
          boxShadow: 'none',
          pb: 1,
          maxHeight: 'calc(100vh - 320px)',
          overflowY: 'auto',
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: '100%', // stretch across available space so the table doesn't look narrow
            borderCollapse: 'separate',
            borderSpacing: '0 12px',
          }}
        >
          <TableHead
            sx={{
              backgroundColor: '#e8f7f3',
              '& th': { backgroundColor: '#e8f7f3' },
            }}
          >
            <TableRow
              sx={{
                '& th': {
                  borderBottom: 'none',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: 'text.secondary',
                  fontWeight: 600,
                  paddingY: 0.5,
                },
              }}
            >
              {showCol('title') && (
                <TableCell sortDirection={orderBy === 'title' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'title'}
                    direction={orderBy === 'title' ? order : 'asc'}
                    onClick={() => handleRequestSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('status') && (
                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('created') && (
                <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('targetDate') && (
                <TableCell sortDirection={orderBy === 'target_date' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'target_date'}
                    direction={orderBy === 'target_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('target_date')}
                  >
                    Target Date
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('dueDate') && (
                <TableCell sortDirection={orderBy === 'due_date' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'due_date'}
                    direction={orderBy === 'due_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('due_date')}
                  >
                    Due Date
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('createdBy') && <TableCell>Created By</TableCell>}
              {showCol('assignedTo') && <TableCell>Assigned To</TableCell>}
              {showCol('collaborators') && <TableCell>Collaborators</TableCell>}

              {showCol('priority') && (
                <TableCell sortDirection={orderBy === 'priority' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'priority'}
                    direction={orderBy === 'priority' ? order : 'asc'}
                    onClick={() => handleRequestSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
              )}

              {showCol('tags') && <TableCell>Tags</TableCell>}
              {showCol('actions') && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedTasks.map((task) => {
              const dueInfo = getDueHealth(task.due_date, task.status);
              const tagsArray = getTaskTags(task);
              const createdByName =
                typeof getUserName === 'function'
                  ? getUserName(task.user_id)
                  : `User ${task.user_id}`;
              const createdByUser =
                resolveUserById(task.user_id, assignableUsers) || {
                  username: createdByName,
                };
              const locked = isTaskLocked(task);
              const statusCfg = getStatusStyle(task.status); // currently unused, but OK

              const priorityColors = {
                low: '#22C55E',
                medium: '#F97316',
                high: '#EF4444',
              };
              const priorityColor =
                priorityColors[(task.priority || 'medium').toLowerCase()] ||
                priorityColors.medium;

              return (
                <TableRow
                  key={task.id}
                  hover
                  onClick={() => onRowClick && onRowClick(task)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: '#ffffff',
                    boxShadow: '0 12px 28px rgba(15,23,42,0.08)',
                    borderRadius: 2,
                    '& td': {
                      borderBottom: 'none',
                      py: 1.4,
                      fontSize: 13,
                    },
                    '& td:first-of-type': {
                      borderTopLeftRadius: 16,
                      borderBottomLeftRadius: 16,
                    },
                    '& td:last-of-type': {
                      borderTopRightRadius: 16,
                      borderBottomRightRadius: 16,
                    },
                  }}
                >
                  {/* Title + small lock indicator if locked */}
                  {showCol('title') && (
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          maxWidth: 260,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: 'text.primary',
                            overflowWrap: 'anywhere',
                            whiteSpace: 'normal',
                          }}
                        >
                          {task.title}
                        </Typography>
                        {task.status === 'completed' && !task.archived && (
                          <Chip
                            label="Pending approval"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(14,165,233,0.12)',
                              color: '#0EA5E9',
                              fontWeight: 700,
                              height: 20,
                            }}
                          />
                        )}
                        {locked && (
                          <LockIcon
                            sx={{ fontSize: 14, color: 'text.disabled' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}

                  {/* Status - inline editable */}
                  {showCol('status') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={task.status || 'to-do'}
                        onChange={(e) =>
                          updateField(task.id, 'status', e.target.value)
                        }
                        disabled={locked}
                        renderValue={(value) => {
                          const cfg = getStatusStyle(value);
                          return (
                            <Chip
                              label={cfg.label}
                              size="small"
                              sx={{
                                bgcolor: cfg.bg,
                                color: cfg.color,
                                fontWeight: 700,
                                borderRadius: 1,
                              }}
                            />
                          );
                        }}
                      >
                        <MenuItem value="to-do">To-Do</MenuItem>
                        <MenuItem value="in-progress">In Progress</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </TableCell>
                  )}

                  {/* Created */}
                  {showCol('created') && (
                    <TableCell>{formatDateDisplay(task.created_at)}</TableCell>
                  )}

                  {/* Target date - inline editable */}
                  {showCol('targetDate') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TextField
                        type="date"
                        size="small"
                        variant="outlined"
                        value={
                          task.target_date ? formatDateIso(task.target_date) : ''
                        }
                        onChange={(e) =>
                          updateField(
                            task.id,
                            'target_date',
                            e.target.value || null
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: { fontSize: 12, padding: '4px 8px' },
                        }}
                        placeholder="Select date"
                        onClick={(evt) => {
                          const input =
                            evt.currentTarget.querySelector('input');
                          if (input) input.showPicker?.();
                        }}
                        disabled={locked}
                      />
                    </TableCell>
                  )}

                  {/* Due date + helper - inline editable */}
                  {showCol('dueDate') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TextField
                        type="date"
                        size="small"
                        variant="outlined"
                        value={
                          task.due_date ? formatDateIso(task.due_date) : ''
                        }
                        onChange={(e) =>
                          updateField(
                            task.id,
                            'due_date',
                            e.target.value || null
                          )
                        }
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          style: { fontSize: 12, padding: '4px 8px' },
                        }}
                        placeholder="Select date"
                        onClick={(evt) => {
                          const input =
                            evt.currentTarget.querySelector('input');
                          if (input) input.showPicker?.();
                        }}
                        disabled={locked}
                      />
                      {dueInfo.label && (
                        <Typography
                          variant="caption"
                          color={dueInfo.labelColor}
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {dueInfo.label}
                        </Typography>
                      )}
                    </TableCell>
                  )}

                  {/* Created by */}
                  {showCol('createdBy') && (
                    <TableCell>
                      <UserAvatar user={createdByUser} size={28} />
                    </TableCell>
                  )}

                  {/* Assigned to - inline editable */}
                  {showCol('assignedTo') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={task.assigned_to || ''}
                        displayEmpty
                        onChange={(e) =>
                          updateField(
                            task.id,
                            'assigned_to',
                            e.target.value === '' ? null : e.target.value
                          )
                        }
                        disabled={locked}
                        renderValue={(value) => {
                          if (!value) return 'Unassigned';
                          const u = resolveUserById(value, assignableUsers);
                          const displayUser =
                            u || { username: `User ${value}` };
                          return <UserAvatar user={displayUser} size={24} />;
                        }}
                        sx={{ minWidth: 100, fontSize: 10 }}
                      >
                        <MenuItem value="">
                          <em>Unassigned</em>
                        </MenuItem>
                        {assignableUsers.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                              }}
                            >
                              <UserAvatar user={u} size={24} />
                              <Typography variant="body2">
                                {buildUserDisplay(u).firstName}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  )}

                  {/* Collaborators */}
                  {showCol('collaborators') && (
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        {Array.isArray(task.collaborators) &&
                        task.collaborators.length > 0 ? (
                          task.collaborators.map((c) => (
                            <UserAvatar
                              key={c.id || c.username}
                              user={c}
                              size={24}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>
                  )}

                  {/* Priority - inline editable */}
                  {showCol('priority') && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={task.priority || 'medium'}
                        onChange={(e) =>
                          updateField(task.id, 'priority', e.target.value)
                        }
                        disabled={locked}
                        renderValue={(value) => (
                          <Chip
                            label={(value || 'medium')
                              .toString()
                              .replace(/^\w/, (c) => c.toUpperCase())}
                            size="small"
                            sx={{
                              bgcolor: `${priorityColor}1a`,
                              color: priorityColor,
                              fontWeight: 700,
                              borderRadius: 1,
                            }}
                          />
                        )}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </TableCell>
                  )}

                  {/* Tags */}
                  {showCol('tags') && (
                    <TableCell>
                      {tagsArray.length > 0 ? tagsArray.join(', ') : '—'}
                    </TableCell>
                  )}

                  {showCol('actions') && canApproveCompletions && (
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {task.status === 'completed' && !task.archived ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            onApproveCompletion &&
                            onApproveCompletion(task)
                          }
                        >
                          Approve
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {sortedTasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColCount} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    No tasks in this project yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export {
  descendingComparator,
  getComparator,
  stableSort,
  PRIORITIES,
  HOME_TABLE_AVAILABLE_COLUMNS,
  getDueHealth,
  getTaskTags,
  TaskTable,
  SimpleListCard,
  RecurringTasksTable,
  ProjectTasksTable,
};
